"use client";

import { useTranslations } from "next-intl";
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

export type ToastTone = "success" | "warning" | "danger" | "info";

const DEFAULT_TOAST_DURATION_MS = 5_000;
const DEFAULT_ERROR_TOAST_DURATION_MS = 30_000;

export type ToastItem = {
  id: string;
  message: string;
  title?: string;
  tone: ToastTone;
  durationMs: number | null;
};

type ToastInput = {
  message: string;
  title?: string;
  tone?: ToastTone;
  durationMs?: number | null;
};

type ToastViewportProps = {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
};

type ToastContextValue = {
  showToast: (toast: ToastInput) => string;
  dismissToast: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

function useToastState() {
  const nextToastId = useRef(0);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const activeToasts = useRef<ToastItem[]>([]);

  const showToast = useCallback((toast: ToastInput) => {
    const tone = toast.tone ?? "info";
    const durationMs = toast.durationMs === undefined
      ? (tone === "danger" ? DEFAULT_ERROR_TOAST_DURATION_MS : DEFAULT_TOAST_DURATION_MS)
      : toast.durationMs;
    const duplicate = activeToasts.current.find((current) => (
      current.message === toast.message
      && current.title === toast.title
      && current.tone === tone
      && current.durationMs === durationMs
    ));

    if (duplicate) {
      return duplicate.id;
    }

    const id = String(++nextToastId.current);
    const nextToast: ToastItem = {
      id,
      message: toast.message,
      title: toast.title,
      tone,
      durationMs,
    };

    activeToasts.current = [...activeToasts.current, nextToast];
    setToasts(activeToasts.current);

    return id;
  }, []);

  const dismissToast = useCallback((id: string) => {
    activeToasts.current = activeToasts.current.filter((toast) => toast.id !== id);
    setToasts(activeToasts.current);
  }, []);

  return { toasts, showToast, dismissToast };
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const { toasts, showToast, dismissToast } = useToastState();

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within ToastProvider.");
  }

  return context;
}

export function ToastViewport({ toasts, onDismiss }: ToastViewportProps) {
  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="toast-viewport" aria-live="polite">
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: string) => void }) {
  const t = useTranslations("common.toast");

  useEffect(() => {
    if (toast.durationMs === null) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      onDismiss(toast.id);
    }, toast.durationMs);

    return () => window.clearTimeout(timeout);
  }, [onDismiss, toast.durationMs, toast.id]);

  return (
    <div
      className={`toast-message toast-message-${toast.tone}`}
      role={toast.tone === "danger" ? "alert" : "status"}
      style={toast.durationMs === null ? undefined : ({ "--toast-duration": `${toast.durationMs}ms` } as CSSProperties)}
    >
      {toast.durationMs === null ? null : <span className="toast-progress" aria-hidden="true" />}
      <span className="toast-status-dot" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-5 text-current">
          <span className="sr-only">{t(`tone.${toast.tone}`)}: </span>
          {toast.title ?? toast.message}
        </p>
        {toast.title && toast.message !== toast.title ? (
          <p className="mt-0.5 text-sm leading-5 opacity-80">
            {toast.message}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        className="toast-close"
        onClick={() => onDismiss(toast.id)}
        aria-label={t("close")}
      >
        ×
      </button>
    </div>
  );
}
